import { BoardCard } from "components/board/board-card";
import { StatusColumnProps } from "types/board";
import { DataCardItem, DataListResponse } from "types/ui/card-item";
import moment from "moment";
import { StatusEnum, TaskStatusEnum } from "types/dict";
import { useEffect, useMemo, useRef } from "react";
import { useSharedState } from "state";

interface BoardProps {
  data: DataListResponse;
}

export default function BoardBackground({ data }: BoardProps) {
  const { searchQuery, handleSearch } = useSharedState();

  const prevMainPath = useRef("");
  const loadingRef = useRef<{ [key: number]: boolean }>({});
  const columnRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const observerRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const searchQueryRef = useRef(searchQuery);

  // Update searchQuery ref when it changes
  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  useEffect(() => {
    // Get the main route path without sub-routes
    const currentMainPath = window.location.pathname.split("/")[1];

    if (prevMainPath.current !== currentMainPath) {
      [0, 1, 2, 3, 4].forEach((status) => {
        if (columnRefs.current[status]) {
          columnRefs.current[status]!.scrollTop = 0;
        }
      });
      prevMainPath.current = currentMainPath;
    }
  }, [window.location.pathname]);

  const isTaskBoard = useMemo(() => {
    return window.location.pathname.includes("tasks") ? true : false;
  }, [window.location.pathname]);

  const isPaymentBoard = useMemo(() => {
    return window.location.pathname.includes("payments") ? true : false;
  }, [window.location.pathname]);

  const statusColumns: StatusColumnProps[] = useMemo(
    () => [
      { title: "К выполнению", status: StatusEnum.TO_DO, color: "warning" },
      { title: "В работе", status: StatusEnum.IN_PROGRESS, color: "primary" },
      {
        title: isTaskBoard ? "На утверждении" : isPaymentBoard ? "К закрытию" : "На подписании",
        status: isTaskBoard ? TaskStatusEnum.FOR_APPROVAL : StatusEnum.IN_EXECUTION,
        color: isTaskBoard ? "purple" : "success",
      },
      {
        title: "Завершённые",
        status: isTaskBoard ? TaskStatusEnum.COMPLETED : StatusEnum.COMPLETED,
        color: isTaskBoard ? "success" : "danger",
      },
    ],
    [isTaskBoard, isPaymentBoard]
  );

  useEffect(() => {
    const observers: { [key: number]: IntersectionObserver } = {};

    statusColumns.forEach((column) => {
      const observer = new IntersectionObserver(
        async (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !loadingRef.current[column.status]) {
            loadingRef.current[column.status] = true;
            try {
              await handleSearch(searchQueryRef.current, column.status);
            } finally {
              loadingRef.current[column.status] = false;
            }
          }
        },
        { threshold: 0.1 }
      );

      if (observerRefs.current[column.status]) {
        observer.observe(observerRefs.current[column.status]!);
      }

      observers[column.status] = observer;
    });

    return () => {
      Object.values(observers).forEach((observer) => observer.disconnect());
    };
  }, [data, statusColumns]);

  const getCardsByStatus = (status: number): DataCardItem[] =>
    isTaskBoard && status === TaskStatusEnum.TO_DO
      ? data[status]?.data.concat(data[TaskStatusEnum.REJECTED]?.data)
      : data[status]?.data;

  const getCardsCount = (status: number): number =>
    isTaskBoard && status === TaskStatusEnum.TO_DO
      ? data[status]?.total_count + data[TaskStatusEnum.REJECTED]?.total_count
      : data[status]?.total_count;

  const getDeadlineColor = (card: DataCardItem) => {
    if (isTaskBoard && card.status === TaskStatusEnum.COMPLETED) return "bg-success-lt";
    if (isTaskBoard && moment().diff(moment(card.dueDate), "days") > -3) return "text-white bg-danger-lt";
    if (isTaskBoard) return "bg-warning-lt";
    return "";
  };

  const shouldLoadNextPage = (status: number): boolean => {
    return data[status].total_count > data[status].data.length ? true : false;
  };

  return (
    <div className="d-flex flex-col">
      <div className="d-flex gap-[10px] h-[calc(100dvh-274px)]">
        {statusColumns.map((column) => (
          <div
            className={`col-12 col-md p-2 h-full rounded-2xl overflow-hidden border border-[#E5ECF0] bg-[rgba(0,67,105,0.03)]`}
            key={column.status}
          >
            <div className="flex flex-col gap-2 max-h-full">
              <div className="flex items-center gap-2 px-4 py-[10px] text-base text-white rounded-xl bg-[#004369]">
                {column.title}
                <span className="flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-md bg-[rgba(255,255,255,0.15)]">
                  {getCardsCount(column.status) || 0}
                </span>
              </div>

              <div
                className="flex flex-col gap-2 overflow-y-auto"
                style={{ scrollbarWidth: "thin" }}
                ref={(el) => (columnRefs.current[column.status] = el)}
              >
                {getCardsByStatus(column.status)?.length ? (
                  <>
                    {getCardsByStatus(column.status)?.map((card, index) => (
                      <BoardCard key={index} card={card} getDeadlineColor={getDeadlineColor} />
                    ))}

                    {shouldLoadNextPage(column.status) && (
                      <div ref={(el) => (observerRefs.current[column.status] = el)}></div>
                    )}
                  </>
                ) : (
                  <div className="text-center text-sm leading-6 text-[#A3A3A3]">Нет соответствующих записей</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
